/**
 * 全局运行状态：分数(ScoreSystem)/残机/炸弹/火力/特殊槽/角色/关卡进度。
 * 纯逻辑，无浏览器依赖，node 可直接 import。
 */
import { ScoreSystem } from './scoring.js';

/** 默认残机数 */
export const DEFAULT_LIVES = 3;
/** 默认炸弹数 */
export const DEFAULT_BOMBS = 3;
/** 火力上限（调低后第一关 boss 前可攒满；溢出转分不变） */
export const POWER_MAX = 96;
/** 炸弹上限 */
export const BOMBS_MAX = 8;
/** 特殊槽上限 */
export const SPECIAL_MAX = 100;
/** 火力溢出转分（每点溢出火力） */
export const POWER_OVERFLOW_SCORE = 1000;
/** 炸弹溢出转分（每个溢出炸弹） */
export const BOMB_OVERFLOW_SCORE = 5000;
/** 拾取点道具转化的特殊槽 */
export const SPECIAL_PER_POINT = 1;
/** 每个 P 道具增加的火力（配合关卡掉落表，第一关 boss 前可接近满级） */
export const POWER_PER_ITEM = 8;
/** 击杀转化的特殊槽 */
export const SPECIAL_PER_KILL = 2;
/** 擦弹转化的特殊槽 */
export const SPECIAL_PER_GRAZE = 0.5;

/**
 * 一局游戏的全部运行状态。计分委托给持有的 ScoreSystem 实例，
 * 本类只负责资源数值与道具分发。
 */
export class GameState {
  constructor() {
    /** @type {ScoreSystem} 计分系统 */
    this.scoring = new ScoreSystem();
    /** @type {object|null} 当前角色定义（由外部注入，含 shot/bomb/special） */
    this.character = null;
    this.reset(null);
  }

  /**
   * 重置为一局初始状态。
   * @param {object|null} charDef 角色定义对象（由选人场景注入）
   * @param {object|null} [difficulty] 难度定义（content/difficulties.js，由游戏场景注入）
   */
  reset(charDef, difficulty = null) {
    this.scoring.reset();
    this.character = charDef;
    /** 当前难度定义（HUD 显示 / 结算提交用） */
    this.difficulty = difficulty;
    this.lives = DEFAULT_LIVES;
    this.bombs = DEFAULT_BOMBS;
    /** 火力 0..96 */
    this.power = 0;
    /** 特殊槽 0..100 */
    this.special = 0;
    /** 当前关序号（0 起） */
    this.stageIndex = 0;
    /** 当前 boss 阶段是否中弹/用炸弹（符卡判定用，gameScene 维护） */
    this.phaseDirty = false;
  }

  /**
   * 增加火力；达到上限后溢出部分转分数。
   * @param {number} n
   */
  addPower(n) {
    this.power += n;
    if (this.power > POWER_MAX) {
      this.scoring.gain((this.power - POWER_MAX) * POWER_OVERFLOW_SCORE);
      this.power = POWER_MAX;
    }
  }

  /**
   * 增加炸弹；达到上限后溢出部分转分数。
   * @param {number} n
   */
  addBomb(n) {
    this.bombs += n;
    if (this.bombs > BOMBS_MAX) {
      this.scoring.gain((this.bombs - BOMBS_MAX) * BOMB_OVERFLOW_SCORE);
      this.bombs = BOMBS_MAX;
    }
  }

  /**
   * 增加特殊槽（擦弹/拾取/击杀转化），封顶 SPECIAL_MAX。
   * @param {number} n
   */
  addSpecial(n) {
    this.special = Math.min(SPECIAL_MAX, this.special + n);
  }

  /**
   * 槽满时消耗 100 点释放特殊能力。
   * @returns {boolean} 是否成功消耗
   */
  consumeSpecial() {
    if (this.special < SPECIAL_MAX) return false;
    this.special -= SPECIAL_MAX;
    return true;
  }

  /**
   * 中弹扣残机：通知计分系统（倍率损失/断连杀/清擦弹记忆）。
   * @returns {boolean} 是否 gameover（本次死亡用掉最后一命）
   */
  loseLife() {
    this.scoring.onPlayerDeath();
    this.lives--;
    return this.lives <= 0;
  }

  /**
   * 道具拾取分发：资源类进本状态，计分类进 scoring。
   * @param {{type: string, x: number, y: number}} item
   * @param {number} viewH 逻辑画面高（点道具高度系数用）
   * @returns {number} 本次拾取直接得分（浮字显示用，资源类为 0）
   */
  collectItem(item, viewH) {
    switch (item.type) {
      case 'P':
        this.addPower(POWER_PER_ITEM);
        return 0;
      case 'B':
        this.addBomb(1);
        return 0;
      case 'POINT': {
        const v = this.scoring.collectPointItem(item.y, viewH);
        this.addSpecial(SPECIAL_PER_POINT);
        return v;
      }
      case 'STAR':
        return this.scoring.collectStar() || 0;
      case '1UP':
        this.lives++;
        return 0;
      default:
        return 0;
    }
  }
}
